'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useCallback } from 'react'
import { Formik } from 'formik'

// walrus
import { accessibilityLabel } from '@components/kit/_support'
import submitForm from '@lib/submit-form'
import { useNavigate } from 'react-router-dom'
import Form, {
  SubmitButton,
  StatusMessage,
  PasswordField,
  FormRow,
  FormFooter
} from '@components/kit/form'
import { BackIcon } from '@components/kit/icons'
import Box from '@components/kit/box'
import Page from '@components/kit/page'
import Pressable from '@components/kit/pressable'
import Screen from '@components/kit/screen'
import Section from '@components/kit/section'
import Text from '@components/kit/text'

// lib
import { useAuthStore } from '@store/Auth'
import { ResetPasswordPayload } from '@store/Auth/types'

/* -----------------------------------------------------------------------------
 * PasswordReset
 * -------------------------------------------------------------------------- */

const PasswordReset = () => {
  const navigate = useNavigate()

  const goBack = () => {
    navigate('/settings')
  }
  const submit = submitForm(
    async (values: ResetPasswordPayload, { setStatus }) => {
      const result = await useAuthStore.getState().resetPassword(values)

      if (!('success' in result)) {
        setStatus({ type: 'error', msg: result.errmsg || result.error })
        return
      }

      navigate('/settings/password-reset/success')
    }
  )

  // flipper answers 'Invalid password' when the current password is wrong;
  // the legacy endpoint used that wording for a too short new password
  const getStatusMsg = useCallback(msg => {
    if (msg === 'Invalid password') {
      return 'Incorrect password. Please try again.'
    }
    return msg
  }, [])

  return (
    <Box flexDirection='column' flex='1 0 auto'>
      <Screen
        {...accessibilityLabel('Reset Password')}
        bg='white'
        keyboardShouldPersistTaps='handled'
        justifyContent='center'
        alignItems='center'
      >
        <Page flex={{ mobile: '1 1 auto', tablet: '0 0 auto' }}>
          <Section>
            <Pressable pb='20' onPress={goBack}>
              <BackIcon fill='violet' />
            </Pressable>
            <Text variant='h1'>Let's reset your password</Text>
          </Section>
          <Section flex='1 1 auto' mt='32'>
            <Formik
              initialValues={{ new_password: '', password: '' }}
              onSubmit={submit}
              validateOnChange={false}
            >
              <Form flex='1 0 auto'>
                <FormRow>
                  <PasswordField
                    name='password'
                    label='Current Password'
                    backgroundColor='shade01'
                    required
                  />
                </FormRow>
                <FormRow>
                  <PasswordField
                    name='new_password'
                    label='New Password'
                    backgroundColor='shade01'
                    required
                    last
                    textContentType='newPassword'
                  />
                </FormRow>
                <FormFooter flex='1 0 auto' justifyContent='flex-end' mt='24'>
                  <StatusMessage getStatusMsg={getStatusMsg} mt='24' />
                  <SubmitButton>Reset your password</SubmitButton>
                </FormFooter>
              </Form>
            </Formik>
          </Section>
        </Page>
      </Screen>
    </Box>
  )
}

export default PasswordReset
